import {
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom";

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import bbox from "@turf/bbox";

import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl";


import { useAuth } from "../../hooks/useAuth";
import {
    getToken,
    removeToken
} from "../../utils/TokenLS";
import {
    showAlert,
    SW_ICON
} from "../../utils/swAlert";

import {
    HTTP_VERBS,
    requestHttp
} from "../../utils/HttpRequest";

import {
    ErrorMessage,
    FilterContainer,
    FilterGroup,
    FilterLabel,
    FilterSelect,
    LegendColor,
    LegendContainer,
    LegendItem,
    LoadingMessage,
    MapContainer,
    MapHeader,
    MapTitle,
    PopupContent,
    PopupLabel,
    PopupRow,
    PopupTitle,
    StatisticsContainer,
    StatisticItem,
    StatisticLabel,
    StatisticValue,
    MapWrapper,
    FloatingLegend,
    LegendTitle,
    BackButton,
    PopupSection,
    PopupSectionTitle,
    PopupTable
} from "./styles";

import {
    FaArrowLeft
} from "react-icons/fa";

const EMPTY_GEOJSON = {
    type: "FeatureCollection",
    features: []
};

const CONCEPT_OPTIONS = [
    {
        value: "",
        label: "Todos"
    },
    {
        value: "112",
        label: "Fertilización mezcla"
    },
    {
        value: "228",
        label: "Fertilización enmienda"
    }
];

const MAP_VIEW_MODES = {
    FERTILIZATION_TYPE: "FERTILIZATION_TYPE",
    PLAN_COMPLIANCE: "PLAN_COMPLIANCE"
};

const SATELLITE_MAP_STYLE = {
    version: 8,

    glyphs:
        "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",

    sources: {
        satellite: {
            type: "raster",
            tiles: [
                "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            ],
            tileSize: 256,
            attribution:
                "Tiles © Esri"
        }
    },

    layers: [
        {
            id: "satellite",
            type: "raster",
            source: "satellite"
        }
    ]
};

const MAPLIBRE_WORKER_FILE =
    "maplibre-gl-csp-worker.js";

const getMapLibreWorkerUrl = () => {
    const publicUrl =
        process.env.PUBLIC_URL ?? "";

    const basePath =
        `${publicUrl.replace(/\/$/, "")}/${MAPLIBRE_WORKER_FILE}`;

    if (typeof window === "undefined") {
        return basePath;
    }

    return new URL(
        basePath,
        window.location.origin
    ).toString();
};

const MAPLIBRE_WORKER_URL =
    getMapLibreWorkerUrl();

if (typeof maplibregl.setWorkerUrl === "function") {
    maplibregl.setWorkerUrl(
        MAPLIBRE_WORKER_URL
    );
}

maplibregl.workerUrl =
    MAPLIBRE_WORKER_URL;

maplibregl.workerClass =
    undefined;

console.log(
    "MapLibre worker URL configurado:",
    MAPLIBRE_WORKER_URL,
    "getWorkerUrl:",
    maplibregl.getWorkerUrl?.()
);

const getCurrentYear = () => new Date().getFullYear();

const buildFertilizationMapUrl = ({
    farmCode,
    cycle,
    conceptCode
}) => {
    const normalizedFarmCode =
        String(farmCode ?? "").trim();

    if (!normalizedFarmCode) {
        return "/fertilization/farms";
    }

    const params =
        new URLSearchParams();

    if (cycle) {
        params.set("cycle", String(cycle));
    }

    if (conceptCode) {
        params.set(
            "conceptCode",
            String(conceptCode)
        );
    }

    const queryString =
        params.toString();

    return queryString
        ? `/fertilization/farms/${normalizedFarmCode}?${queryString}`
        : `/fertilization/farms/${normalizedFarmCode}`;
};

const parseArrayProperty = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string") {
        return [];
    }

    try {
        const parsedValue = JSON.parse(value);

        return Array.isArray(parsedValue)
            ? parsedValue
            : [];
    } catch {
        return [];
    }
};

const normalizePopupProperties = (properties = {}) => ({
    ...properties,
    applicationCount:
        Number(properties.applicationCount) || 0,
    totalQuantity:
        Number(properties.totalQuantity) || 0,
    netArea:
        Number(properties.netArea) || 0,
    grossArea:
        Number(properties.grossArea) || 0,
    cycles:
        parseArrayProperty(properties.cycles),
    conceptCodes:
        parseArrayProperty(properties.conceptCodes),
    fertilizationTypes:
        parseArrayProperty(properties.fertilizationTypes),
    composition:
        parseArrayProperty(
            properties.composition
        ),
    nutrientContributions:
        parseArrayProperty(
            properties.nutrientContributions
        ),
    expectedQuantity:
        Number(properties.expectedQuantity) || 0,

    expectedKilograms:
        Number(properties.expectedKilograms) || 0,

    appliedQuantity:
        Number(properties.appliedQuantity) || 0,

    appliedKilograms:
        Number(properties.appliedKilograms) || 0,

    pendingQuantity:
        Number(properties.pendingQuantity) || 0,

    pendingKilograms:
        Number(properties.pendingKilograms) || 0,

    progressPercentage:
        Number(properties.progressPercentage) || 0,

    applicationStatus:
        properties.applicationStatus ?? "",

    applicationStatusLabel:
        properties.applicationStatusLabel ?? "",

    applicationStatusColor:
        properties.applicationStatusColor ?? "#d1d5db",

    mixtureCode:
        properties.mixtureCode ?? "",

    mixtureName:
        properties.mixtureName ?? "",

    fertilizationCode:
        properties.fertilizationCode ?? "",

    fertilizationName:
        properties.fertilizationName ?? "",

    bultosPerHectare:
        Number(properties.bultosPerHectare) || 0,

    kilogramsPerBag:
        Number(properties.kilogramsPerBag) || 0,

    kilogramsPerHectare:
        Number(properties.kilogramsPerHectare) || 0,

    hasExceededQuantity:
        properties.hasExceededQuantity === true ||
        properties.hasExceededQuantity === "true",

    excessQuantity:
        Math.round(
            Number(properties.excessQuantity) || 0
        )
});

const createPopupInfo = (event, feature) => ({
    longitude: event.lngLat.lng,
    latitude: event.lngLat.lat,
    properties: normalizePopupProperties(
        feature.properties
    )
});

const isTokenError = (error) => {
    const status =
        error?.response?.status;

    return status === 401 || status === 403;
};

export const FertilizationMap = () => {
    const { farmCode } = useParams();

    const [searchParams] =
        useSearchParams();

    //console.log( "Código finca recibido:", farmCode );


    const { auth, closeSeccionAuth } = useAuth();
    const navigate = useNavigate();

    const sessionClosingRef = useRef(false);
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const popupRef = useRef(null);
    const layerHandlersRef = useRef(null);


    const queryCycle =
        searchParams.get("cycle") ?? "";

    const queryConceptCode =
        searchParams.get("conceptCode") ?? "";

    const hasInitialPlanFilters =
        Boolean(queryCycle) &&
        Boolean(queryConceptCode);

    const [selectedFarmCode, setSelectedFarmCode] =
        useState(() => String(farmCode ?? "").trim());

    useEffect(() => {
        const normalizedFarmCode =
            String(farmCode ?? "").trim();

        if (normalizedFarmCode) {
            setSelectedFarmCode(
                normalizedFarmCode
            );
        }
    }, [farmCode]);

    useEffect(() => {
        setCycle(queryCycle);
        setConceptCode(queryConceptCode);

        if (queryCycle && queryConceptCode) {
            setMapViewMode(
                MAP_VIEW_MODES.PLAN_COMPLIANCE
            );
        }
    }, [
        queryCycle,
        queryConceptCode
    ]);

    const [year, setYear] =
        useState(getCurrentYear());

    const [cycle, setCycle] =
        useState(queryCycle);

    const [conceptCode, setConceptCode] =
        useState(queryConceptCode);

    const [mapViewMode, setMapViewMode] =
        useState(
            hasInitialPlanFilters
                ? MAP_VIEW_MODES.PLAN_COMPLIANCE
                : MAP_VIEW_MODES.FERTILIZATION_TYPE
        );

    const [sucursales, setSucursales] = useState([]);

    const [farm, setFarm] =
        useState(null);

    const [statistics, setStatistics] =
        useState(null);

    const [geojson, setGeojson] =
        useState(EMPTY_GEOJSON);

    const [mapReady, setMapReady] =
        useState(false);

    const [selectedLot, setSelectedLot] =
        useState(null);

    const [hoveredLot, setHoveredLot] =
        useState(null);

    const [selectedLotId, setSelectedLotId] =
        useState("");

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const handleCloseSession = useCallback(() => {
        if (sessionClosingRef.current) {
            return;
        }

        sessionClosingRef.current = true;

        closeSeccionAuth();
        removeToken();

        showAlert(
            "Inicie sesión nuevamente",
            "Su token puede estar vencido",
            SW_ICON.INFO,
            () => {
                navigate("/Login");
            }
        );
    }, [
        closeSeccionAuth,
        navigate
    ]);

    const getSucursalesAll = useCallback(async () => {
        const token = getToken();

        if (!token) {
            handleCloseSession();
            return;
        }

        try {
            const response = await requestHttp({
                method: HTTP_VERBS.GET,
                endpoint: "/sucursal"
            });

            const { data } = response;

            const sucursalesData =
                data?.data ??
                data ??
                [];

            setSucursales(
                Array.isArray(sucursalesData)
                    ? sucursalesData
                    : []
            );
        } catch (error) {
            console.error(
                "Error cargando sucursales:",
                error
            );

            if (isTokenError(error)) {
                handleCloseSession();
                return;
            }

            setSucursales([]);
        }
    }, [handleCloseSession]);

    useEffect(() => {
        getSucursalesAll();
    }, [auth, getSucursalesAll]);

    const visiblePopup =
        selectedLot ?? hoveredLot;

    const setMapCursor = useCallback((cursor) => {
        const map = mapInstanceRef.current;

        if (map) {
            map.getCanvas().style.cursor = cursor;
        }
    }, []);

    const fitMapToLots = useCallback(
        (featureCollection) => {
            const map = mapInstanceRef.current;

            if (
                !map ||
                !featureCollection?.features?.length
            ) {
                return;
            }

            try {
                map.resize();

                const bounds =
                    bbox(featureCollection);

                const [
                    west,
                    south,
                    east,
                    north
                ] = bounds;

                const hasValidBounds = [
                    west,
                    south,
                    east,
                    north
                ].every(Number.isFinite);

                if (!hasValidBounds) {
                    console.warn(
                        "Bounds inválidos para el mapa:",
                        bounds
                    );
                    return;
                }

                map.fitBounds(
                    [
                        [west, south],
                        [east, north]
                    ],
                    {
                        padding: 80,
                        duration: 700,
                        maxZoom: 17
                    }
                );
            } catch (fitBoundsError) {
                console.error(
                    "Error ajustando mapa a los lotes:",
                    fitBoundsError
                );
            }
        },
        []
    );

    const loadMapData = useCallback(async () => {

        const token = getToken();

        if (!token) {
            handleCloseSession();
            return;
        }

        if (!selectedFarmCode || !year) {
            return;
        }

        setLoading(true);
        setError("");
        setSelectedLot(null);
        setHoveredLot(null);
        setSelectedLotId("");

        try {
            const response = await requestHttp({
                method: HTTP_VERBS.GET,
                endpoint:
                    `/api/farms/${selectedFarmCode}/fertilization-map`,
                params: {
                    year,
                    cycle: cycle || undefined,
                    conceptCode:
                        conceptCode || undefined
                }
            });

            const result = response?.data?.data;

            if (!result) {
                throw new Error(
                    "La respuesta del servidor no contiene información del mapa"
                );
            }

            const mapGeojson =
                result.geojson ?? EMPTY_GEOJSON;

            setFarm(result.farm ?? null);
            setStatistics(result.statistics ?? null);
            setGeojson(mapGeojson);

        } catch (requestError) {
            console.error(
                "Error cargando mapa:",
                requestError
            );

            if (isTokenError(requestError)) {
                handleCloseSession();
                return;
            }

            setFarm(null);
            setStatistics(null);
            setGeojson(EMPTY_GEOJSON);

            setError(
                requestError?.response?.data?.message ??
                requestError?.message ??
                "No fue posible cargar el mapa"
            );
        } finally {
            setLoading(false);
        }
    }, [
        selectedFarmCode,
        year,
        cycle,
        conceptCode,
        handleCloseSession
    ]);

    useEffect(() => {
        loadMapData();
    }, [auth, loadMapData]);

    const fillLayer = useMemo(
        () => ({
            id: "fertilization-lots-fill",
            type: "fill",
            paint: {
                "fill-color":
                    mapViewMode ===
                        MAP_VIEW_MODES.PLAN_COMPLIANCE
                        ? [
                            "match",
                            ["get", "applicationStatus"],

                            "COMPLETED",
                            "#16a34a",

                            "INCOMPLETE",
                            "#facc15",

                            "NOT_STARTED",
                            "#d1d5db",

                            "WITHOUT_PLAN",
                            "#9ca3af",

                            [
                                "coalesce",
                                [
                                    "get",
                                    "applicationStatusColor"
                                ],
                                "#9ca3af"
                            ]
                        ]
                        : [
                            "coalesce",
                            ["get", "displayColor"],
                            "#d1d5db"
                        ],

                "fill-opacity": [
                    "case",
                    [
                        "==",
                        [
                            "get",
                            "applicationStatus"
                        ],
                        "WITHOUT_PLAN"
                    ],
                    0.25,

                    [
                        "==",
                        [
                            "get",
                            "applicationStatus"
                        ],
                        "NOT_STARTED"
                    ],
                    0.35,

                    0.75
                ]
            }
        }),
        [mapViewMode]
    );

    const borderLayer = useMemo(
        () => ({
            id: "fertilization-lots-border",
            type: "line",
            paint: {
                "line-color": [
                    "match",
                    [
                        "to-string",
                        [
                            "coalesce",
                            [
                                "get",
                                "displayConceptCode"
                            ],
                            ""
                        ]
                    ],
                    "112",
                    "#8a5a00",
                    "228",
                    "#991b1b",
                    "#374151"
                ],
                "line-width": 3,
                "line-opacity": 1
            }
        }),
        []
    );

    const selectedLayer = useMemo(
        () => ({
            id: "fertilization-selected-lot",
            type: "line",
            filter: [
                "==",
                ["get", "udpId"],
                selectedLotId
            ],
            paint: {
                "line-color": "#111827",
                "line-width": 5,
                "line-opacity": 0.95
            }
        }),
        [selectedLotId]
    );

    const labelLayer = useMemo(
        () => ({
            id: "fertilization-lots-label",
            type: "symbol",
            layout: {
                "text-field": [
                    "concat",
                    [
                        "coalesce",
                        ["get", "udpName"],
                        ["get", "udpCode"],
                        ""
                    ],
                    "\n",
                    [
                        "to-string",
                        [
                            "round",
                            [
                                "to-number",
                                [
                                    "coalesce",
                                    ["get", "netArea"],
                                    0
                                ]
                            ]
                        ]
                    ],
                    " ha"
                ],
                "text-size": 12,
                "text-anchor": "center",
                "text-allow-overlap": false
            },
            paint: {
                "text-color": "#111827",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2
            }
        }),
        []
    );

    const excessLabelLayer = useMemo(
        () => ({
            id: "fertilization-lots-excess-label",
            type: "symbol",
            layout: {
                "text-field": [
                    "case",
                    [
                        "any",
                        [
                            "==",
                            ["get", "hasExceededQuantity"],
                            true
                        ],
                        [
                            "==",
                            [
                                "to-string",
                                ["get", "hasExceededQuantity"]
                            ],
                            "true"
                        ]
                    ],
                    [
                        "concat",
                        "EXC +",
                        [
                            "to-string",
                            [
                                "round",
                                [
                                    "to-number",
                                    [
                                        "coalesce",
                                        ["get", "excessQuantity"],
                                        0
                                    ]
                                ]
                            ]
                        ],
                        " bul"
                    ],
                    ""
                ],
                "text-size": 13,
                "text-anchor": "center",
                "text-offset": [0, 1.8],
                "text-allow-overlap": true,
                "text-ignore-placement": true
            },
            paint: {
                "text-color": "#ffffff",
                "text-halo-color": "#991b1b",
                "text-halo-width": 2
            }
        }),
        []
    );

    const removeMapLayerIfExists = useCallback((map, layerId) => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
    }, []);

    const removeMapSourceIfExists = useCallback((map, sourceId) => {
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
    }, []);

    const buildPopupHtml = useCallback((properties = {}) => {
        const normalizedProperties =
            normalizePopupProperties(properties);

        const escapeHtml = (value) =>
            String(value ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");

        const formatNumber = (value) =>
            Number(value ?? 0).toLocaleString("es-CO");

        const compositionRows =
            normalizedProperties.composition
                .map((item, index) => `
                    <tr key="composition-${index}">
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb;">
                            ${escapeHtml(item.componentName ?? item.componentCode ?? "—")}
                        </td>
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb; text-align:right;">
                            ${formatNumber(item.percentage)}
                        </td>
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb; text-align:right;">
                            ${formatNumber(item.quantityKg)}
                        </td>
                    </tr>
                `)
                .join("");

        const nutrientRows =
            normalizedProperties.nutrientContributions
                .map((item, index) => `
                    <tr key="nutrient-${index}">
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb;">
                            ${escapeHtml(item.nutrientName ?? item.name ?? item.code ?? "—")}
                        </td>
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb; text-align:right;">
                            ${formatNumber(item.percentage ?? item.value)}
                        </td>
                        <td style="padding:6px 8px; border-bottom:1px solid #e5e7eb; text-align:right;">
                            ${formatNumber(item.quantityKg ?? item.kilograms ?? item.kg)}
                        </td>
                    </tr>
                `)
                .join("");

        const compositionTable = compositionRows
            ? `
                <div style="margin-top:10px;">
                    <div style="font-weight:700; margin-bottom:6px; color:#111827;">
                        Producto: ${escapeHtml(normalizedProperties.mixtureName || normalizedProperties.fertilizationName || "Sin mezcla")}
                    </div>
                    <table style="width:100%; border-collapse:collapse; font-size:12px;">
                        <thead>
                            <tr style="background:#f3f4f6; color:#374151;">
                                <th style="padding:6px 8px; text-align:left; border-bottom:1px solid #d1d5db;">Componente</th>
                                <th style="padding:6px 8px; text-align:right; border-bottom:1px solid #d1d5db;">%</th>
                                <th style="padding:6px 8px; text-align:right; border-bottom:1px solid #d1d5db;">Aportes Kg</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${compositionRows}
                        </tbody>
                    </table>
                </div>
            `
            : "";

        const nutrientTable = nutrientRows
            ? `
                <div style="margin-top:10px;">
                    <div style="font-weight:700; margin-bottom:6px; color:#111827;">
                        Aportes nutricionales
                    </div>
                    <table style="width:100%; border-collapse:collapse; font-size:12px;">
                        <thead>
                            <tr style="background:#f3f4f6; color:#374151;">
                                <th style="padding:6px 8px; text-align:left; border-bottom:1px solid #d1d5db;">Nutriente</th>
                                <th style="padding:6px 8px; text-align:right; border-bottom:1px solid #d1d5db;">%</th>
                                <th style="padding:6px 8px; text-align:right; border-bottom:1px solid #d1d5db;">Kg</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${nutrientRows}
                        </tbody>
                    </table>
                </div>
            `
            : "";

        const cyclesLabel =
            normalizedProperties.cycles.length > 0
                ? normalizedProperties.cycles.join(", ")
                : "Ninguno";

        const exceededRow =
            normalizedProperties.hasExceededQuantity
                ? `
                    <div style="display:flex; justify-content:space-between; gap:12px; color:#991b1b; font-weight:700;">
                        <span>Excedido:</span>
                        <span>+${formatNumber(normalizedProperties.excessQuantity)} bultos</span>
                    </div>
                `
                : "";

        return `
            <div style="font-family: Arial, sans-serif; min-width:300px; max-width:420px; color:#111827;">
                <div style="font-weight:800; font-size:15px; margin-bottom:8px;">
                    ${escapeHtml(normalizedProperties.udpName || `Lote ${normalizedProperties.udpCode || ""}`)}
                </div>

                <div style="display:grid; gap:5px; font-size:13px;">
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Área neta:</b></span>
                        <span>${formatNumber(normalizedProperties.netArea)} ha</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Área bruta:</b></span>
                        <span>${formatNumber(normalizedProperties.grossArea)} ha</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Fertilización:</b></span>
                        <span>${escapeHtml(normalizedProperties.lastApplicationLabel || "Sin aplicación")}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Ciclos:</b></span>
                        <span>${escapeHtml(cyclesLabel)}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Estado plan:</b></span>
                        <span>${escapeHtml(normalizedProperties.applicationStatusLabel || "Sin plan")}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Dosis:</b></span>
                        <span>${formatNumber(normalizedProperties.bultosPerHectare)} bultos/ha</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Kg/ha:</b></span>
                        <span>${formatNumber(normalizedProperties.kilogramsPerHectare)}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Esperado:</b></span>
                        <span>${formatNumber(normalizedProperties.expectedQuantity)} bultos</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Aplicado:</b></span>
                        <span>${formatNumber(normalizedProperties.appliedQuantity)} bultos</span>
                    </div>
                    ${exceededRow}
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Pendiente:</b></span>
                        <span>${formatNumber(normalizedProperties.pendingQuantity)} bultos</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span><b>Avance:</b></span>
                        <span>${formatNumber(normalizedProperties.progressPercentage)}%</span>
                    </div>
                </div>

                ${compositionTable}
                ${nutrientTable}
            </div>
        `;
    }, []);

    const verifyMapLibreWorkerFile = useCallback(async () => {
        try {
            const response =
                await fetch(
                    MAPLIBRE_WORKER_URL,
                    {
                        cache: "no-store"
                    }
                );
            /*
            console.log(
                "Verificación worker MapLibre:",
                {
                    url: MAPLIBRE_WORKER_URL,
                    ok: response.ok,
                    status: response.status,
                    contentType:
                        response.headers.get(
                            "content-type"
                        )
                }
            );
            */
            if (!response.ok) {
                console.warn(
                    "El worker de MapLibre no está disponible. Verifica que el archivo exista en public/maplibre-gl-csp-worker.js"
                );
            }
        } catch (workerError) {
            console.error(
                "Error verificando worker MapLibre:",
                workerError
            );
        }
    }, []);

    const renderGeojsonOnPureMap = useCallback(
        (featureCollection) => {
            const map =
                mapInstanceRef.current;

            if (
                !map ||
                !featureCollection?.features?.length
            ) {
                return;
            }

            const sourceId =
                "fertilization-lots";

            const fillLayerId =
                "fertilization-lots-fill";

            const borderLayerId =
                "fertilization-lots-border";

            const selectedLayerId =
                "fertilization-selected-lot";

            const labelLayerId =
                "fertilization-lots-label";

            const excessLabelLayerId =
                "fertilization-lots-excess-label";

            const paintLayers = () => {
                if (layerHandlersRef.current) {
                    const {
                        clickHandler,
                        mouseMoveHandler,
                        mouseLeaveHandler,
                        mapClickOutsideHandler
                    } = layerHandlersRef.current;

                    map.off(
                        "click",
                        fillLayerId,
                        clickHandler
                    );

                    if (mapClickOutsideHandler) {
                        map.off(
                            "click",
                            mapClickOutsideHandler
                        );
                    }

                    map.off(
                        "mousemove",
                        fillLayerId,
                        mouseMoveHandler
                    );

                    map.off(
                        "mouseleave",
                        fillLayerId,
                        mouseLeaveHandler
                    );

                    layerHandlersRef.current = null;
                }

                removeMapLayerIfExists(
                    map,
                    excessLabelLayerId
                );

                removeMapLayerIfExists(
                    map,
                    labelLayerId
                );

                removeMapLayerIfExists(
                    map,
                    selectedLayerId
                );

                removeMapLayerIfExists(
                    map,
                    borderLayerId
                );

                removeMapLayerIfExists(
                    map,
                    fillLayerId
                );

                removeMapSourceIfExists(
                    map,
                    sourceId
                );

                map.addSource(
                    sourceId,
                    {
                        type: "geojson",
                        data: featureCollection
                    }
                );

                map.addLayer({
                    ...fillLayer,
                    source: sourceId
                });

                map.addLayer({
                    ...borderLayer,
                    source: sourceId
                });

                map.addLayer({
                    ...selectedLayer,
                    source: sourceId
                });

                map.addLayer({
                    ...labelLayer,
                    source: sourceId
                });

                map.addLayer({
                    ...excessLabelLayer,
                    source: sourceId
                });

                const clickHandler = (event) => {
                    const feature =
                        event.features?.[0];

                    if (!feature) {
                        return;
                    }

                    const udpId =
                        feature.properties?.udpId ?? "";

                    setSelectedLotId(udpId);
                    setSelectedLot(
                        createPopupInfo(
                            event,
                            feature
                        )
                    );
                    setHoveredLot(null);

                    if (map.getLayer(selectedLayerId)) {
                        map.setFilter(
                            selectedLayerId,
                            [
                                "==",
                                [
                                    "get",
                                    "udpId"
                                ],
                                udpId
                            ]
                        );
                    }

                    if (popupRef.current) {
                        popupRef.current.remove();
                    }

                    popupRef.current =
                        new maplibregl.Popup({
                            closeButton: true,
                            closeOnClick: false,
                            offset: 12,
                            maxWidth: "460px"
                        })
                            .setLngLat(event.lngLat)
                            .setHTML(
                                buildPopupHtml(
                                    feature.properties
                                )
                            )
                            .addTo(map);
                };

                const mapClickOutsideHandler = (event) => {
                    const features =
                        map.queryRenderedFeatures(
                            event.point,
                            {
                                layers: [
                                    fillLayerId
                                ]
                            }
                        );

                    if (features.length > 0) {
                        return;
                    }

                    setSelectedLot(null);
                    setHoveredLot(null);
                    setSelectedLotId("");

                    if (map.getLayer(selectedLayerId)) {
                        map.setFilter(
                            selectedLayerId,
                            [
                                "==",
                                [
                                    "get",
                                    "udpId"
                                ],
                                ""
                            ]
                        );
                    }

                    if (popupRef.current) {
                        popupRef.current.remove();
                        popupRef.current = null;
                    }
                };

                const mouseMoveHandler = (event) => {
                    const feature =
                        event.features?.[0];

                    map.getCanvas().style.cursor =
                        feature
                            ? "pointer"
                            : "";

                    if (
                        feature &&
                        !selectedLot
                    ) {
                        setHoveredLot(
                            createPopupInfo(
                                event,
                                feature
                            )
                        );
                    }
                };

                const mouseLeaveHandler = () => {
                    map.getCanvas().style.cursor = "";

                    if (!selectedLot) {
                        setHoveredLot(null);
                    }
                };

                map.on(
                    "click",
                    fillLayerId,
                    clickHandler
                );

                map.on(
                    "click",
                    mapClickOutsideHandler
                );

                map.on(
                    "mousemove",
                    fillLayerId,
                    mouseMoveHandler
                );

                map.on(
                    "mouseleave",
                    fillLayerId,
                    mouseLeaveHandler
                );

                layerHandlersRef.current = {
                    clickHandler,
                    mouseMoveHandler,
                    mouseLeaveHandler,
                    mapClickOutsideHandler
                };

                fitMapToLots(featureCollection);
            };

            if (map.isStyleLoaded()) {
                paintLayers();
            } else {
                map.once(
                    "load",
                    paintLayers
                );
            }
        },
        [
            borderLayer,
            buildPopupHtml,
            excessLabelLayer,
            fillLayer,
            fitMapToLots,
            labelLayer,
            removeMapLayerIfExists,
            removeMapSourceIfExists,
            selectedLayer,
            selectedLot
        ]
    );

    useEffect(() => {
        if (
            !mapReady ||
            !geojson?.features?.length
        ) {
            return;
        }

        renderGeojsonOnPureMap(geojson);
    }, [
        mapReady,
        geojson,
        renderGeojsonOnPureMap
    ]);

    const handleMapClick = useCallback((event) => {
        const feature = event.features?.[0];

        if (!feature) {
            setSelectedLot(null);
            setHoveredLot(null);
            setSelectedLotId("");
            return;
        }

        const udpId =
            feature.properties?.udpId ?? "";

        setSelectedLotId(udpId);
        setSelectedLot(
            createPopupInfo(event, feature)
        );
        setHoveredLot(null);
    }, []);

    const handleMouseMove = useCallback(
        (event) => {
            const feature = event.features?.[0];

            if (!feature) {
                setMapCursor("");

                if (!selectedLot) {
                    setHoveredLot(null);
                }

                return;
            }

            setMapCursor("pointer");

            if (!selectedLot) {
                setHoveredLot(
                    createPopupInfo(event, feature)
                );
            }
        },
        [selectedLot, setMapCursor]
    );

    const handleMouseLeave = useCallback(() => {
        setMapCursor("");

        if (!selectedLot) {
            setHoveredLot(null);
        }
    }, [selectedLot, setMapCursor]);

    const closePopup = useCallback(() => {
        setSelectedLot(null);
        setHoveredLot(null);
        setSelectedLotId("");
    }, []);

    const completedCycles = useMemo(() => {
        const totalsByConcept =
            statistics?.totalsByConcept ?? {};

        const allCycles = Object.values(
            totalsByConcept
        ).flatMap(
            (concept) =>
                concept?.cycles ?? []
        );

        return [
            ...new Set(
                allCycles
                    .map(Number)
                    .filter(
                        (cycleNumber) =>
                            Number.isInteger(
                                cycleNumber
                            ) &&
                            cycleNumber > 0
                    )
            )
        ].sort((a, b) => a - b);
    }, [statistics]);

    const cyclesCount =
        completedCycles.length;

    const mixtureStatistics =
        statistics?.totalsByConcept?.["112"];

    const amendmentStatistics =
        statistics?.totalsByConcept?.["228"];

    const getMaxCycle = (cycles = []) => {
        const validCycles = cycles
            .map(Number)
            .filter(
                (cycleNumber) =>
                    Number.isInteger(cycleNumber) &&
                    cycleNumber > 0
            );

        return validCycles.length > 0
            ? Math.max(...validCycles)
            : 0;
    };

    const mixtureMaxCycle = getMaxCycle(
        mixtureStatistics?.cycles
    );

    const amendmentMaxCycle = getMaxCycle(
        amendmentStatistics?.cycles
    );

    const initialLongitude =
        farm?.mapCenter?.coordinates?.[0] ??
        -76.645;

    const initialLatitude =
        farm?.mapCenter?.coordinates?.[1] ??
        7.82;

    useEffect(() => {
        if (
            !mapContainerRef.current ||
            mapInstanceRef.current
        ) {
            return;
        }

        verifyMapLibreWorkerFile();
        /*
        console.log(
            "Creando MapLibre puro con worker:",
            {
                workerUrl: MAPLIBRE_WORKER_URL,
                getWorkerUrl:
                    maplibregl.getWorkerUrl?.(),
                workerClass:
                    maplibregl.workerClass
            }
        );
        */
        const map =
            new maplibregl.Map({
                container: mapContainerRef.current,
                style: SATELLITE_MAP_STYLE,
                center: [
                    initialLongitude,
                    initialLatitude
                ],
                zoom: farm?.mapZoom ?? 15,
                workerUrl: MAPLIBRE_WORKER_URL,
                workerCount: 1
            });

        mapInstanceRef.current =
            map;

        map.addControl(
            new maplibregl.NavigationControl(),
            "top-right"
        );

        map.addControl(
            new maplibregl.FullscreenControl(),
            "top-right"
        );

        map.on(
            "load",
            () => {
                /*
                console.log(
                    "Pure MapLibre cargado"
                );
                */
                setMapReady(true);
                map.resize();

                if (geojson?.features?.length) {
                    renderGeojsonOnPureMap(geojson);
                }
            }
        );

        map.on(
            "error",
            (mapError) => {
                console.error(
                    "Pure MapLibre error:",
                    {
                        error: mapError,
                        message:
                            mapError?.error?.message,
                        sourceId:
                            mapError?.sourceId,
                        tile:
                            mapError?.tile
                    }
                );
            }
        );

        return () => {
            if (popupRef.current) {
                popupRef.current.remove();
                popupRef.current = null;
            }

            if (layerHandlersRef.current) {
                const {
                    clickHandler,
                    mouseMoveHandler,
                    mouseLeaveHandler,
                    mapClickOutsideHandler
                } = layerHandlersRef.current;

                map.off(
                    "click",
                    "fertilization-lots-fill",
                    clickHandler
                );

                if (mapClickOutsideHandler) {
                    map.off(
                        "click",
                        mapClickOutsideHandler
                    );
                }

                map.off(
                    "mousemove",
                    "fertilization-lots-fill",
                    mouseMoveHandler
                );

                map.off(
                    "mouseleave",
                    "fertilization-lots-fill",
                    mouseLeaveHandler
                );

                layerHandlersRef.current = null;
            }

            map.remove();
            mapInstanceRef.current = null;
            setMapReady(false);
        };
    }, [
        verifyMapLibreWorkerFile
    ]);

    if (!farmCode) {
        return (
            <MapContainer>
                <ErrorMessage>
                    No se recibió el código de la finca en la URL.
                </ErrorMessage>
            </MapContainer>
        );
    }

    const planStatistics =
        statistics?.plan;

    const complianceStatistics =
        statistics?.compliance;

    return (
        <MapContainer>
            <MapHeader>
                <MapTitle>
                    Mapa de fertilización
                    {farm?.name
                        ? ` - ${farm.name}`
                        : ""}
                </MapTitle>

                <FilterContainer>
                    <FilterGroup>
                        <FilterLabel>
                            Finca
                        </FilterLabel>

                        <FilterSelect
                            value={selectedFarmCode}
                            onChange={(event) => {
                                const newFarmCode =
                                    event.target.value;

                                setSelectedFarmCode(
                                    newFarmCode
                                );

                                if (newFarmCode) {
                                    navigate(
                                        buildFertilizationMapUrl({
                                            farmCode:
                                                newFarmCode,
                                            cycle,
                                            conceptCode
                                        })
                                    );
                                }
                            }}
                        >
                            <option value="">
                                Seleccione una finca
                            </option>

                            {sucursales.map((sucursal) => {
                                const code =
                                    String(
                                        sucursal.strCodigo ??
                                        ""
                                    ).trim();

                                const name =
                                    sucursal.strNombre ??
                                    "";

                                if (!code) {
                                    return null;
                                }

                                return (
                                    <option
                                        key={code}
                                        value={code}
                                    >
                                        {code} - {name}
                                    </option>
                                );
                            })}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Año</FilterLabel>

                        <FilterSelect
                            value={year}
                            onChange={(event) =>
                                setYear(
                                    Number(
                                        event.target.value
                                    )
                                )
                            }
                        >
                            {[
                                getCurrentYear() - 2,
                                getCurrentYear() - 1,
                                getCurrentYear(),
                                getCurrentYear() + 1
                            ].map((yearOption) => (
                                <option
                                    key={yearOption}
                                    value={yearOption}
                                >
                                    {yearOption}
                                </option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>Ciclo</FilterLabel>

                        <FilterSelect
                            value={cycle}
                            onChange={(event) => {
                                const newCycle =
                                    event.target.value;

                                setCycle(newCycle);

                                if (selectedFarmCode) {
                                    navigate(
                                        buildFertilizationMapUrl({
                                            farmCode:
                                                selectedFarmCode,
                                            cycle:
                                                newCycle,
                                            conceptCode
                                        }),
                                        {
                                            replace: true
                                        }
                                    );
                                }
                            }}
                        >
                            <option value="">
                                Todos
                            </option>

                            {Array.from(
                                { length: 15 },
                                (_, index) => index + 1
                            ).map((cycleOption) => (
                                <option
                                    key={cycleOption}
                                    value={cycleOption}
                                >
                                    Ciclo {cycleOption}
                                </option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>
                            Fertilización
                        </FilterLabel>

                        <FilterSelect
                            value={conceptCode}
                            onChange={(event) => {
                                const newConceptCode =
                                    event.target.value;

                                setConceptCode(
                                    newConceptCode
                                );

                                if (selectedFarmCode) {
                                    navigate(
                                        buildFertilizationMapUrl({
                                            farmCode:
                                                selectedFarmCode,
                                            cycle,
                                            conceptCode:
                                                newConceptCode
                                        }),
                                        {
                                            replace: true
                                        }
                                    );
                                }
                            }}
                        >
                            {CONCEPT_OPTIONS.map(
                                (option) => (
                                    <option
                                        key={
                                            option.value ||
                                            "all"
                                        }
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </FilterSelect>
                    </FilterGroup>

                    <FilterGroup>
                        <FilterLabel>
                            Visualización
                        </FilterLabel>

                        <FilterSelect
                            value={mapViewMode}
                            onChange={(event) =>
                                setMapViewMode(
                                    event.target.value
                                )
                            }
                        >
                            <option
                                value={
                                    MAP_VIEW_MODES
                                        .FERTILIZATION_TYPE
                                }
                            >
                                Tipo de fertilización
                            </option>

                            <option
                                value={
                                    MAP_VIEW_MODES
                                        .PLAN_COMPLIANCE
                                }
                            >
                                Cumplimiento del plan
                            </option>
                        </FilterSelect>
                    </FilterGroup>
                    <BackButton
                        type="button"
                        onClick={() =>
                            navigate(
                                "/fertilization/farms"
                            )
                        }
                    >
                        <FaArrowLeft />
                        <span>
                            Volver a todas las fincas
                        </span>
                    </BackButton>
                </FilterContainer>

                {mapViewMode ===
                    MAP_VIEW_MODES.PLAN_COMPLIANCE &&
                    (!cycle || !conceptCode) && (
                        <ErrorMessage>
                            Para evaluar cumplimiento del plan debe seleccionar ciclo y fertilización.
                        </ErrorMessage>
                    )}



                {statistics && (
                    <StatisticsContainer>
                        {complianceStatistics && (
                            <>
                                <StatisticItem>
                                    <StatisticLabel>
                                        Ciclos registrados
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {cyclesCount}
                                    </StatisticValue>

                                    <small>
                                        {completedCycles.length > 0
                                            ? completedCycles.join(", ")
                                            : "Sin ciclos"}

                                    </small>
                                </StatisticItem>

                                {planStatistics && (
                                    <StatisticItem>
                                        <StatisticLabel>
                                            Plan seleccionado
                                        </StatisticLabel>

                                        <StatisticValue>
                                            {planStatistics.hasPlan
                                                ? `Ciclo ${planStatistics.cycleNumber}`
                                                : "Sin plan"}
                                        </StatisticValue>

                                        <small>
                                            {planStatistics.mixtureName ||
                                                planStatistics.fertilizationName ||
                                                "Seleccione ciclo y fertilización"}
                                        </small>
                                    </StatisticItem>
                                )}

                                <StatisticItem>
                                    <StatisticLabel>
                                        Avance plan
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {Number(
                                            complianceStatistics.progressPercentage ?? 0
                                        ).toLocaleString("es-CO")}
                                        %
                                    </StatisticValue>

                                    <small>
                                        {Number(
                                            complianceStatistics.appliedQuantity ?? 0
                                        ).toLocaleString("es-CO")}
                                        {" / "}
                                        {Number(
                                            complianceStatistics.expectedQuantity ?? 0
                                        ).toLocaleString("es-CO")}
                                    </small>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Pendiente
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {Number(
                                            complianceStatistics.pendingQuantity ?? 0
                                        ).toLocaleString("es-CO")}
                                    </StatisticValue>

                                    <small>bultos</small>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Fertilizacion Mezcla Aplicada
                                    </StatisticLabel>
                                    <StatisticValue>
                                        {Number(
                                            mixtureStatistics
                                                ?.totalQuantity ?? 0
                                        ).toLocaleString("es-CO")}
                                    </StatisticValue>
                                    <small>
                                        {mixtureStatistics
                                            ?.lotsCount ?? 0}{" "}
                                        lotes - Ciclo:{" "}
                                        {mixtureMaxCycle || "Sin ciclos"}
                                    </small>
                                    <small>

                                    </small>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Enmienda Aplicada
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {Number(
                                            amendmentStatistics
                                                ?.totalQuantity ?? 0
                                        ).toLocaleString("es-CO")}
                                    </StatisticValue>

                                    <small>
                                        {amendmentStatistics
                                            ?.lotsCount ?? 0}{" "}
                                        lotes - Ciclo:{" "}
                                        {amendmentMaxCycle || "Sin ciclos"}
                                    </small>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Área neta finca
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {Number(
                                            complianceStatistics.netArea ?? 0
                                        ).toLocaleString("es-CO")}
                                    </StatisticValue>

                                    <small>ha</small>
                                </StatisticItem>
                                <StatisticItem>
                                    <StatisticLabel>
                                        Área bruta finca
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {Number(
                                            complianceStatistics.grossArea ?? 0
                                        ).toLocaleString("es-CO")}
                                    </StatisticValue>

                                    <small>ha</small>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Lotes activos
                                    </StatisticLabel>
                                    <StatisticValue>
                                        {statistics.totalActiveLots ??
                                            statistics.totalLots ??
                                            0}
                                        <small>
                                            Con geometría: {statistics.lotsWithGeometry ??
                                                0}
                                        </small>
                                    </StatisticValue>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Lotes completos
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {complianceStatistics.completedLots ?? 0}
                                    </StatisticValue>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        Lotes incompletos
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {complianceStatistics.incompleteLots ?? 0}
                                    </StatisticValue>
                                </StatisticItem>

                                <StatisticItem>
                                    <StatisticLabel>
                                        No iniciados
                                    </StatisticLabel>

                                    <StatisticValue>
                                        {complianceStatistics.notStartedLots ?? 0}
                                    </StatisticValue>
                                </StatisticItem>
                            </>
                        )}

                    </StatisticsContainer>
                )}
            </MapHeader>

            {loading && (
                <LoadingMessage>
                    Cargando mapa...
                </LoadingMessage>
            )}

            {error && (
                <ErrorMessage>{error}</ErrorMessage>
            )}

            {!error && (
                <MapWrapper>
                    <div
                        ref={mapContainerRef}
                        style={{
                            width: "100%",
                            height: "680px"
                        }}
                    />

                    <FloatingLegend>
                        <LegendTitle>
                            {mapViewMode ===
                                MAP_VIEW_MODES.PLAN_COMPLIANCE
                                ? "Cumplimiento del plan"
                                : "Fertilización"}
                        </LegendTitle>

                        {mapViewMode ===
                            MAP_VIEW_MODES.PLAN_COMPLIANCE ? (
                            <>
                                <LegendItem>
                                    <LegendColor $color="#16a34a" />
                                    Completo
                                </LegendItem>

                                <LegendItem>
                                    <LegendColor $color="#facc15" />
                                    Incompleto
                                </LegendItem>

                                <LegendItem>
                                    <LegendColor $color="#d1d5db" />
                                    No iniciado
                                </LegendItem>

                                <LegendItem>
                                    <LegendColor $color="#9ca3af" />
                                    Sin plan
                                </LegendItem>
                            </>
                        ) : (
                            <>
                                <LegendItem>
                                    <LegendColor $color="#f0a808" />
                                    Mezcla
                                </LegendItem>

                                <LegendItem>
                                    <LegendColor $color="#f18d91" />
                                    Enmienda
                                </LegendItem>

                                <LegendItem>
                                    <LegendColor $color="#d1d5db" />
                                    Sin aplicación
                                </LegendItem>
                            </>
                        )}
                    </FloatingLegend>
                </MapWrapper>
            )}
        </MapContainer>
    );
};
